import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import {
  Search, Package, MapPin, Phone, CheckCircle2, Clock, Truck,
  PackagePlus, Calculator, AlertCircle, ArrowRight,
  Shield, Zap, Headphones, ChevronRight, ClipboardPaste,
  Camera, User,
} from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from 'pages/HomePage/components/Header';
import Footer from 'pages/HomePage/components/Footer';
import { apiPublicCMS } from 'api/request';
import urlApp from 'api/urlApp';
import { TrackingData, TrackingJourney } from 'types/tracking';
import {
  formatTrackingDate,
  formatWeight,
  formatCurrency,
  getJourneyLabel,
  getJourneyDetail,
  mapStatusMeta,
} from './helpers';
import './TraCuuPage.scss';

const getTrustItems = (t: any) => [
  { icon: Shield,     label: t('traCuu.trust.secure'),      sub: t('traCuu.trust.secureSub') },
  { icon: Zap,        label: t('traCuu.trust.realtime'),    sub: t('traCuu.trust.realtimeSub') },
  { icon: Headphones, label: t('traCuu.trust.support'),     sub: t('traCuu.trust.supportSub') },
];

type ResultState = 'idle' | 'found' | 'notfound';

// ─── Component ────────────────────────────────────────────────────────────────
const TraCuuPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [input,   setInput]   = useState('');
  const [result,  setResult]  = useState<ResultState>('idle');
  const [loading, setLoading] = useState(false);
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  // Auto-trigger search when redirected from homepage with ?q=<code>
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setInput(q);
      performSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useLayoutEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          (e.target as HTMLElement).setAttribute('data-visible', 'true');
          observer.unobserve(e.target);
        }
      }),
      { threshold: 0, rootMargin: '200px 0px 200px 0px' }
    );
    document.documentElement.classList.add('js-animations-ready');
    document.querySelectorAll('[data-reveal],[data-stagger]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  /**
   * Ảnh giao hàng. Máy chủ trả `image_url_list` (đầy đủ) và `image_urls` (tấm
   * đầu, giữ cho bản trang cũ còn nằm trong bộ nhớ đệm trình duyệt). Đọc cái
   * đầy đủ trước, không có thì rơi về tấm đơn.
   */
  const anhGiaoHang: string[] = (() => {
    const ds = trackingData?.image_url_list;
    if (Array.isArray(ds) && ds.length) return ds.filter(Boolean);
    const mot = trackingData?.image_urls;
    return mot ? String(mot).split(',').map((u) => u.trim()).filter(Boolean) : [];
  })();

  const performSearch = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;
    setLoading(true);
    setResult('idle');
    setTrackingData(null);
    setErrorMsg(null);
    try {
      const { data: res } = await apiPublicCMS.get<{ message: string; ref: unknown; data: TrackingData }>(
        urlApp.getPublicTrackingApiPath,
        { params: { code: trimmed } }
      );
      if (res.data && res.data.order_code) {
        setTrackingData(res.data);
        setResult('found');
        setTimeout(() => {
          resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      } else {
        setResult('notfound');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Không tìm thấy';
      if (msg.toLowerCase().includes('không tìm thấy') || err?.response?.status === 404) {
        setResult('notfound');
      } else {
        setErrorMsg(msg);
        setResult('notfound');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    performSearch(input);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        setInput(text);
      }
    } catch (err) {
      console.error('Failed to read clipboard contents: ', err);
    }
  };

  return (
    <>
      <Header />

      {/* ══ HERO — search-first layout ══════════════════════════════════════ */}
      <section className="tc-hero" aria-label={t('traCuu.hero.sectionAria')}>
        <div className="tc-hero__pattern" aria-hidden="true" />
        <div className="tc-hero__glow"   aria-hidden="true" />

        <div className="tc-hero__container" data-reveal data-anim="up">

          {/* Compact headline */}
          <span className="tc-hero__eyebrow">
            <Search size={13} />
            {t('traCuu.hero.eyebrow')}
          </span>

          <h1 className="tc-hero__title">
            {t('traCuu.hero.title')}
            <span className="tc-hero__accent"> {t('traCuu.hero.titleAccent')}</span>
          </h1>

          <p className="tc-hero__sub">{t('traCuu.hero.sub')}</p>

          {/* ── BIG SEARCH BAR ── */}
          <form className="tc-search-bar" onSubmit={handleSearch} aria-label={t('traCuu.search.sectionAria')}>
            <div className="tc-search-bar__input-wrap">
              <Search className="tc-search-bar__icon" size={20} />
              <input
                id="tracking-input"
                type="text"
                className="tc-search-bar__input tc-search-bar__input--with-paste"
                placeholder={t('traCuu.search.placeholder')}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                aria-label={t('traCuu.search.inputAria')}
                autoComplete="off"
              />
              <button
                type="button"
                className="tc-search-bar__paste"
                onClick={handlePaste}
                title={t('traCuu.search.pasteBtn')}
              >
                <ClipboardPaste size={16} />
                <span>{t('traCuu.search.pasteBtn')}</span>
              </button>
            </div>
            <button
              id="tracking-submit"
              type="submit"
              className="tc-search-bar__btn"
              disabled={loading}
              aria-label={t('traCuu.search.btnAria')}
            >
              {loading
                ? <span className="tc-search-bar__spinner" aria-hidden="true" />
                : <Search size={18} />
              }
              <span>{loading ? t('traCuu.search.btnLoading') : t('traCuu.search.btn')}</span>
            </button>
          </form>


          {/* Trust strip */}
          <div className="tc-hero__trust">
            {getTrustItems(t).map(({ icon: Icon, label, sub }) => (
              <div key={label} className="tc-hero__trust-item">
                <span className="tc-hero__trust-icon"><Icon size={14} /></span>
                <span className="tc-hero__trust-label">{label}</span>
                <span className="tc-hero__trust-sep">·</span>
                <span className="tc-hero__trust-sub">{sub}</span>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* ══ RESULT ══════════════════════════════════════════════════════════ */}
      <section ref={resultRef} className="tc-result" aria-live="polite" aria-label={t('traCuu.result.sectionAria')}>
        <div className="tc-result__container">

          {/* Quick links when idle */}
          {result === 'idle' && !loading && (
            <div className="tc-quicklinks tc-result--appear">
              <Link to="/tao-don" className="tc-quicklinks__item">
                <span className="tc-quicklinks__item-icon"><PackagePlus size={18} /></span>
                <span className="tc-quicklinks__item-text">
                  <strong>{t('traCuu.quick.create')}</strong>
                  <span>{t('traCuu.quick.createSub')}</span>
                </span>
                <ChevronRight size={16} className="tc-quicklinks__item-arrow" />
              </Link>
              <Link to="/tra-cuu/uoc-tinh-cuoc" className="tc-quicklinks__item">
                <span className="tc-quicklinks__item-icon"><Calculator size={18} /></span>
                <span className="tc-quicklinks__item-text">
                  <strong>{t('traCuu.quick.estimate')}</strong>
                  <span>{t('traCuu.quick.estimateSub')}</span>
                </span>
                <ChevronRight size={16} className="tc-quicklinks__item-arrow" />
              </Link>
              <Link to="/lien-he" className="tc-quicklinks__item">
                <span className="tc-quicklinks__item-icon"><Headphones size={18} /></span>
                <span className="tc-quicklinks__item-text">
                  <strong>{t('traCuu.quick.contact')}</strong>
                  <span>{t('traCuu.quick.contactSub')}</span>
                </span>
                <ChevronRight size={16} className="tc-quicklinks__item-arrow" />
              </Link>
            </div>
          )}

          {/* Not found */}
          {result === 'notfound' && (
            <div className="tc-notfound tc-result--appear">
              <div className="tc-notfound__icon-wrap">
                <AlertCircle size={40} strokeWidth={1.5} />
              </div>
              <h2 className="tc-notfound__title">{t('traCuu.notFound.title')}</h2>
              <p className="tc-notfound__sub">
                {errorMsg || t('traCuu.notFound.sub')}
              </p>
              <button
                id="retry-search"
                className="tc-notfound__retry"
                onClick={() => { setResult('idle'); setInput(''); setErrorMsg(null); }}
              >
                <ArrowRight size={15} /> {t('traCuu.notFound.retry')}
              </button>
            </div>
          )}

          {/* Found */}
          {result === 'found' && trackingData && (
            <div className="tc-result--appear">

              {/* Status banner */}
              <StatusBanner data={trackingData} />

              {/* Main grid */}
              <div className="tc-main-grid">

                {/* Timeline panel */}
                <div className="tc-panel">
                  <h3 className="tc-panel__title"><Truck size={15} /> {t('traCuu.result.journeyTitle')}</h3>
                  {trackingData.journeys && trackingData.journeys.length > 0 ? (
                    <div className="tc-journey">
                      {trackingData.journeys.map((journey, i) => {
                        const isLatest = i === 0;
                        const isDone = i > 0;
                        return (
                          <div
                            key={i}
                            className={`tc-journey-item ${isDone ? 'tc-journey-item--done' : ''} ${isLatest ? 'tc-journey-item--latest' : ''}`}
                          >
                            {i < trackingData.journeys.length - 1 && (
                              <div className={`tc-journey-item__line ${isDone ? 'tc-journey-item__line--done' : ''}`} />
                            )}
                            <div className="tc-journey-item__node">
                              {isDone
                                ? <CheckCircle2 size={14} strokeWidth={2.5} />
                                : isLatest
                                  ? <div className="tc-journey-item__pulse" />
                                  : <div className="tc-journey-item__dot" />
                              }
                            </div>
                            <div className="tc-journey-item__body">
                              <p className="tc-journey-item__label">{getJourneyLabel(journey)}</p>
                              <p className="tc-journey-item__time">{formatTrackingDate(journey.thoiGian)}</p>
                              {(() => {
                                const detail = getJourneyDetail(journey);
                                return detail ? <p className="tc-journey-item__detail">{detail}</p> : null;
                              })()}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '0.85rem', color: '#475569', margin: 0 }}>
                      Chưa có cập nhật hành trình
                    </p>
                  )}
                </div>

                {/* Info panels */}
                <div className="tc-info-panels">
                  <div className="tc-panel tc-panel--compact">
                    <h3 className="tc-panel__title"><Package size={15} /> {t('traCuu.result.senderInfo')}</h3>
                    <p className="tc-info__name">{trackingData.sender_name || '—'}</p>
                    {trackingData.sender_phone && (
                      <p className="tc-info__row"><Phone size={13} /> {trackingData.sender_phone}</p>
                    )}
                  </div>
                  <div className="tc-panel tc-panel--compact">
                    <h3 className="tc-panel__title"><MapPin size={15} /> {t('traCuu.result.receiverInfo')}</h3>
                    <p className="tc-info__name">{trackingData.receiver_name || '—'}</p>
                    {trackingData.receiver_phone && (
                      <p className="tc-info__row"><Phone size={13} /> {trackingData.receiver_phone}</p>
                    )}
                    {trackingData.receiver_address && (
                      <p className="tc-info__row"><MapPin size={13} /> {trackingData.receiver_address}</p>
                    )}
                  </div>
                  <div className="tc-panel tc-panel--compact">
                    <h3 className="tc-panel__title"><Clock size={15} /> {t('traCuu.result.orderDetails')}</h3>
                    <div className="tc-meta-grid">
                      <div className="tc-meta-item">
                        <span className="tc-meta-item__key">{t('traCuu.result.service')}</span>
                        <span className="tc-meta-item__val">{trackingData.service_name || '—'}</span>
                      </div>
                      <div className="tc-meta-item">
                        <span className="tc-meta-item__key">{t('traCuu.result.weight')}</span>
                        <span className="tc-meta-item__val">{formatWeight(trackingData.weight)}</span>
                      </div>
                      <div className="tc-meta-item">
                        <span className="tc-meta-item__key">COD</span>
                        <span className={`tc-meta-item__val ${trackingData.cod > 0 ? 'tc-meta-item__val--highlight' : ''}`}>
                          {trackingData.cod > 0 ? formatCurrency(trackingData.cod) : 'Không thu COD'}
                        </span>
                      </div>
                      <div className="tc-meta-item">
                        <span className="tc-meta-item__key">{t('traCuu.result.createdAt')}</span>
                        <span className="tc-meta-item__val">{formatTrackingDate(trackingData.created_date)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Ảnh bằng chứng giao hàng.
                      Bưu tá thường chụp hai tấm: gói hàng và chữ ký người nhận.
                      Trước đây chỉ hiện tấm đầu nên khách mất phần còn lại. */}
                  {anhGiaoHang.length > 0 && (
                    <div className="tc-panel tc-panel--compact">
                      <div className="tc-evidence">
                        <h3 className="tc-evidence__title">
                          <Camera size={15} /> Ảnh bằng chứng giao hàng
                          {anhGiaoHang.length > 1 ? ` (${anhGiaoHang.length})` : ''}
                        </h3>
                        <div className={anhGiaoHang.length > 1 ? 'tc-evidence__grid' : ''}>
                          {anhGiaoHang.map((url, i) => (
                            <div className="tc-evidence__img-wrap" key={url}>
                              <img
                                src={url}
                                alt={`Ảnh bằng chứng giao hàng ${i + 1}`}
                                className="tc-evidence__img"
                                loading="lazy"
                                onClick={() => window.open(url, '_blank')}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="tc-evidence__caption">Nhấn vào ảnh để xem kích thước đầy đủ</div>
                      </div>
                    </div>
                  )}

                  <div className="tc-actions">
                    <Link to="/tao-don" id="result-create-order" className="tc-actions__btn tc-actions__btn--primary">
                      <PackagePlus size={16} /> {t('traCuu.result.createBtn')}
                    </Link>
                    <Link to="/tra-cuu/uoc-tinh-cuoc" id="result-estimate" className="tc-actions__btn tc-actions__btn--outline">
                      <Calculator size={16} /> {t('traCuu.result.estimateBtn')}
                    </Link>
                  </div>
                </div>

              </div>
            </div>
          )}

        </div>
      </section>

      <Footer />
    </>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StatusBanner: React.FC<{ data: TrackingData }> = ({ data }) => {
  const meta = mapStatusMeta(data.status, data.journeys || [], data.status_name);
  return (
    <div className="tc-banner">
      <div className="tc-banner__left">
        <span className="tc-banner__eyebrow">Mã vận đơn</span>
        <h2 className="tc-banner__code">{data.order_code}</h2>
      </div>
      <div className="tc-banner__right">
        <span className={`tc-banner__badge tc-banner__badge--${meta.variant}`}>
          <Clock size={13} /> {meta.label}
        </span>
        {data.expected_receiver_time && (
          <p className="tc-banner__eta">
            Dự kiến giao: <strong>{formatTrackingDate(data.expected_receiver_time)}</strong>
          </p>
        )}
      </div>
    </div>
  );
};

export default TraCuuPage;
